import { Button, Input } from '@casumo/dojo-react';
import { Check, Close } from '@casumo/dojo-react-icons';
import { ChangeEvent, memo, useMemo, useRef, useState } from 'react';
import useFetchErrorContent from '../../api/hooks/useFetchErrorContent';
import Drawer from '../../design-system/drawer.tsx';
import Loader from '../../design-system/loader.tsx';
import Skeleton from '../../design-system/skeleton.tsx';
import useFieldValidator from '../../hooks/useFieldValidator.tsx';
import { useFlowContext } from '../../hooks/useFlowContext.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { TEmailInputField } from '../../types/types.ts';
import { TEST_ID } from '../../utils/constants.ts';
import { gatekeeper } from '../../utils/gatekeeper.ts';

const EmailInput = ({
  name,
  value,
  label,
  validation,
  autoFocused,
  clearsPasswordOnClear,
  onBlur,
  onKeyDown,
}: TEmailInputField & {
  onBlur?: (field: Partial<TEmailInputField>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  value?: string;
}) => {
  const {
    metadata,
    errors,
    setErrors,
    formData,
    setMetadata,
    setFormData,
    customValidations,
    setCustomValidations,
  } = useRegistrationStore();
  const { config } = useFlowContext();
  const { ajv } = useFieldValidator(config);
  const { data: duplicateEmail, loading: duplicateEmailLoading } = useFetchErrorContent('duplicate_email');
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const email: string = useMemo(() => value || formData?.[name] || '', [value, formData, name]);

  const handleBlur = async () => {
    setMetadata({ isEmailValid: false });
    onBlur?.({ name, validation });
    const validate = ajv.compile(validation || {});
    const valid = validate(email);

    if (!email || !valid) {
      return;
    }

    setMetadata({ isEmailValid: true });

    setErrors({ ...errors, [name]: '' });
    setIsLoading(false);

    setHasError(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrors({ [name]: '' });
    setCustomValidations({ [name]: '' });
    setFormData({ [name]: '', plainTextPassword: clearsPasswordOnClear ? '' : formData?.plainTextPassword });
    setFormData({ [name]: e.currentTarget.value });
  };

  const onEnterAnotherEmail = () => {
    setFormData({ [name]: '' });
    setErrors({ [name]: '' });
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleLoginNavigation = () => {
    setMetadata({ isOpen: false })
    gatekeeper?.navigation.navigate('/log-in', {});
  }

  const handleClear = () => {
    setMetadata({ isEmailValid: false });
    setFormData({ [name]: '', plainTextPassword: clearsPasswordOnClear ? '' : formData?.plainTextPassword });

    if (!clearsPasswordOnClear) {
      return;
    }

    setErrors({
      ...errors,
      plainTextPassword: '',
    });

    setCustomValidations({
      ...customValidations,
      [name]: '',
    });
  }

  if (duplicateEmailLoading) {
    return <Skeleton height="h-[52px]" />;
  }

  return (
    <>
      <Input
        id="email"
        ref={inputRef}
        name={name}
        autoComplete="email"
        label={label}
        value={email}
        error={errors[name] ? true : undefined}
        data-error={errors[name] ? true : undefined}
        errorMessage={errors[name]}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isLoading}
        clearable
        onClear={handleClear}
        autoFocus={!formData?.[name] && autoFocused}
        onKeyDown={onKeyDown}
        sufix={
          isLoading ? (
            <Loader />
          ) : !hasError && metadata?.isEmailValid ? (
            <span
              className="flex items-center text-[24px] text-green-30"
              data-testid={TEST_ID.PREFIX + 'checkmark'}
            >
              <Check />
            </span>
          ) : null
        }
        data-testid={TEST_ID.INPUT + name}
        wrapperClass="hide-close-button-on-blur"
      />
      <Drawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onClose={() => setFormData({ [name]: '' })}
        dialogClass="!h-fit"
      >
        <div className="flex flex-col h-full w-full gap-lg p-sm justify-between overflow-hidden">
          <div className="flex flex-col">
            <div className="flex justify-between text-white pb-sm font-bold">
              <h1 className="text-h-lg">{duplicateEmail?.title}</h1>
              <span
                className="p-2xs cursor-pointer"
                onClick={onEnterAnotherEmail}
              >
                <Close />
              </span>
            </div>
            <div className="text-white text-body-base">
              {duplicateEmail?.message}
            </div>
          </div>
          <div className="flex md:flex-row flex-col justify-between gap-xs">
            <Button className="w-full" onClick={handleLoginNavigation}>
              {duplicateEmail?.btnSecondaryText}
            </Button>
            <Button
              variant="primary-dark"
              className="w-full"
              onClick={onEnterAnotherEmail}
            >
              {duplicateEmail?.btnMainText}
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

const Memoized = memo(EmailInput);
Memoized.displayName = 'EmailInput';

export default Memoized;
